import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { consentApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { useConsentStore } from "@/store/consentStore";

const REQUIRED = [
  { key: "terms_of_service", label: "Terms of Service", desc: "Required to use Trata Health." },
  { key: "privacy_policy", label: "Privacy Policy (DPDP)", desc: "How we protect and process your data." },
];

const OPTIONAL = [
  { key: "healthDataCollection", label: "Health data processing", desc: "Store & process your reports and health records." },
  { key: "aiAnalysis", label: "AI analysis", desc: "Let Dr Sanjeevani analyse your reports & symptoms." },
  { key: "emergencyDataSharing", label: "Emergency data sharing", desc: "Share your data with responders during emergencies." },
  { key: "personalizedRecommendations", label: "Personalised recommendations", desc: "Tailor care suggestions to you." },
  { key: "analyticsTracking", label: "Usage analytics", desc: "Help us improve the app with anonymised usage data." },
  { key: "promotionalMessages", label: "Promotional messages", desc: "Occasional offers and health tips." },
];

export default function ConsentSettings() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const hydrateFromServer = useConsentStore((s) => s.hydrateFromServer);

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    if (!user?.phone) return;
    consentApi.get(user.phone).then((res) => {
      const map = res.data?.consents || {};
      const next = {};
      [...REQUIRED, ...OPTIONAL].forEach(({ key }) => {
        next[key] = !!map[key]?.consented;
      });
      setState(next);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.phone]);

  const toggle = async (key, value) => {
    setState((s) => ({ ...s, [key]: value }));
    setSavingKey(key);
    try {
      await consentApi.recordBulk({
        phone: user.phone,
        userId: user._id,
        consentVersion: "2.0",
        consents: [{ consentType: key, consented: value }],
      });
      // Refresh global consent store so feature gates reflect the change.
      const fresh = await consentApi.get(user.phone);
      hydrateFromServer(fresh.data?.consents);
      toast.success(value ? "Consent granted" : "Consent withdrawn");
    } catch (e) {
      setState((s) => ({ ...s, [key]: !value }));
      toast.error("Could not update consent");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <button className="flex items-center gap-1 text-sm text-muted-foreground" onClick={() => navigate(-1)} data-testid="consent-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-secondary flex items-center justify-center">
          <ShieldCheck className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold">Privacy & Consent</h1>
          <p className="text-muted-foreground text-sm">You control how your data is used (DPDP Act).</p>
        </div>
      </div>

      {loading ? (
        <>
          <Skeleton className="h-28 rounded-3xl" />
          <Skeleton className="h-48 rounded-3xl" />
        </>
      ) : (
        <>
          <Card className="rounded-3xl p-6">
            <h2 className="font-semibold mb-2">Agreements</h2>
            {REQUIRED.map((c) => (
              <div key={c.key} className="flex items-center justify-between py-3">
                <div className="pr-4">
                  <p className="text-sm font-medium">{c.label}</p>
                  <p className="text-xs text-muted-foreground">{c.desc}</p>
                </div>
                <span className="flex items-center gap-1 text-sm text-success shrink-0" data-testid={`consent-status-${c.key}`}>
                  <CheckCircle2 className="h-4 w-4" /> Accepted
                </span>
              </div>
            ))}
          </Card>

          <Card className="rounded-3xl p-6">
            <h2 className="font-semibold mb-2">Data & feature permissions</h2>
            <div className="divide-y divide-border">
              {OPTIONAL.map((c) => (
                <div key={c.key} className="flex items-center justify-between py-4">
                  <div className="pr-4">
                    <p className="text-sm font-medium">{c.label}</p>
                    <p className="text-xs text-muted-foreground">{c.desc}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {savingKey === c.key && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    <Switch
                      checked={!!state[c.key]}
                      onCheckedChange={(v) => toggle(c.key, v)}
                      disabled={savingKey === c.key}
                      data-testid={`consent-switch-${c.key}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <p className="text-xs text-muted-foreground text-center px-6">
            Withdrawing a consent stops the related processing. Some features (like AI report analysis) need their consent to work.
          </p>
        </>
      )}
    </div>
  );
}
